




































































































































































































import axios from 'axios';
import * as Sentry from '@sentry/vue';
import EventBus from '@/eventBus';
// Reuse the same email validator as the profile editor and login flow.
// eslint-disable-next-line import/no-extraneous-dependencies
import isEmail from 'validator/lib/isEmail';
import i18n from 'vue-i18n';
import VerifyEmail from '@/components/Templates/signup/VerifyEmail.vue';
import SignUpStage from '@/components/Templates/signup/SignUpStage.vue';
import CoHeadline from '@/components/Atoms/co-headline/CoHeadline.vue';
import CoText from '@/components/Atoms/co-text/CoText.vue';
import CoButton from '@/components/Atoms/co-button/CoButton.vue';
import CoRoundButton from '@/components/Atoms/co-round-button/CoRoundButton.vue';
import CoIcon from '@/components/Atoms/co-icon/CoIcon.vue';
import CoThumbnail from '@/components/Molecules/co-thumbnail/CoThumbnail.vue';
import CoInput from '@/components/Molecules/co-input/CoInput.vue';
import { BFF_AUTH_ERROR_CODES, PublicSignUpConfig, SignUpStage as SignUpStageType } from '@/types/auth';

const requestConfig = { withCredentials: true, xsrfCookieName: '_csrf', xsrfHeaderName: 'X-XSRF-TOKEN' };
const bff = (path: string) => `${window.location.origin}/bff/${path}`;

export default {
    name: 'PublicSignup',
    components: {
        VerifyEmail,
        SignUpStage,
        CoHeadline,
        CoText,
        CoButton,
        CoRoundButton,
        CoIcon,
        CoThumbnail,
        CoInput,
    },
    data() {
        return {
            step: 'email',
            transitionName: 'signup-forward',
            email: '',
            password: '',
            fields: { name: '', salutation: 'u' } as Record<string, any>,
            expiresIn: 0,
            stages: [] as SignUpStageType[],
            stageIndex: 0,
            loading: false,
            error: '',
            redirectTimer: null as ReturnType<typeof setTimeout> | null,
        };
    },
    computed: {
        currentStage() {
            return this.stages[this.stageIndex];
        },
        redirectTarget() {
            const target = this.$route.query.redirect;
            if (typeof target !== 'string') return '/';
            try {
                const decoded = decodeURIComponent(target);
                return decoded.startsWith('/') && !decoded.startsWith('//') ? decoded : '/';
            } catch (error) {
                return '/';
            }
        },
        isEmailValid() {
            return Boolean(this.email) && isEmail(this.email);
        },
        passwordValid() {
            return typeof this.password === 'string' && Array.from(this.password).length >= 15;
        },
        nameValid() {
            return this.validateName(this.fields.name);
        },
        userInitials() {
            const names = this.fields.name.trim().split(/\s+/u);
            return `${names[0]?.[0] || ''}${names[names.length - 1]?.[0] || ''}`.toUpperCase();
        },
        stageValid() {
            if (!this.currentStage) return false;
            // Required fields must have a meaningful value; zero is valid for number fields.
            return (this.currentStage.Fields || []).every((field) => {
                if (!field.Required) return true;
                const value = this.fields[field.Key];
                // A saved dropdown default can outlive its option and appear empty to the user.
                if (field.Type === 4)
                    return typeof value === 'string' && value.trim() !== '' && (field.Options || []).includes(value);
                if (Array.isArray(value)) return value.length > 0;
                if (typeof value === 'string') return value.trim().length > 0;
                return value === 0 || Boolean(value);
            });
        },
    },
    watch: {
        step() {
            this.error = '';
            this.focusHeading();
        },
    },
    async mounted() {
        this.loadConfig();
        const inviteId = this.$route.query.invite;
        if (typeof inviteId === 'string' && inviteId) {
            try {
                // Use the existing invite validation response to prefill an untouched email field.
                const invite = await this.$store.dispatch('validateSignupInvite', inviteId);
                if (invite.Valid && typeof invite.Email === 'string' && isEmail(invite.Email) && !this.email) {
                    this.email = invite.Email;
                }
            } catch (error) {
                // Leave the email field available for manual entry when invite validation fails.
            }
        }
    },
    beforeDestroy() {
        if (this.redirectTimer) clearTimeout(this.redirectTimer);
    },
    methods: {
        redirectAfterSignup() {
            if (this.redirectTimer) clearTimeout(this.redirectTimer);
            this.redirectTimer = null;
            this.$router.push(this.step === 'success' ? this.redirectTarget : '/login');
        },
        validateEmail(value: string) {
            return Boolean(value) && isEmail(value);
        },
        // Count Unicode characters so the length rule also works for passphrases with emoji.
        validatePassword(value: string) {
            return typeof value === 'string' && Array.from(value).length >= 15;
        },
        // The first word is the given name; every remaining word belongs to the family name.
        validateName(value: string) {
            return typeof value === 'string' && value.trim().split(/\s+/u).length >= 2;
        },
        // A step may own its heading, so focus the rendered heading after the slide finishes.
        focusHeading() {
            this.$nextTick(() => (this.$el as HTMLElement).querySelector<HTMLElement>('h1')?.focus());
        },
        afterStepEnter() {
            this.focusHeading();
            if (this.step === 'otp') (this.$refs.verifyEmail as any)?.startCooldown(60);
        },
        // Signup keeps gateway transactions and proof inside the BFF's temporary session.
        async startEmail() {
            // Keep the button path aligned with CoInput's validator when there is no form element.
            (this.$refs.emailInput as any)?.inputValidation(this.email);
            if (!this.validateEmail(this.email)) {
                return;
            }
            this.loading = true;
            try {
                this.email = this.email.toLowerCase();
                const { data } = await axios.post(bff('signup/start'), { email: this.email }, requestConfig);
                this.expiresIn = data.expires_in;
                this.transitionName = 'signup-forward';
                this.step = 'otp';
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async startCode() {
            this.loading = true;
            this.error = '';
            try {
                const { data } = await axios.post(bff('signup/start'), { email: this.email }, requestConfig);
                this.expiresIn = data.expires_in;
                (this.$refs.verifyEmail as any)?.startCooldown(this.expiresIn);
            } catch (error) {
                this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async verifyEmail(otp: string) {
            if (!otp) return;
            this.loading = true;
            this.error = '';
            try {
                const { data } = await axios.post(bff('signup/verify'), { otp }, requestConfig);
                if (data.authenticated) {
                    this.$store.commit('SET_AUTH_STATE', { authenticated: true, session: data });
                    this.$router.push(this.redirectTarget);
                    return;
                }
                if (data.status !== 'registration_required') throw new Error('Unexpected verification result');

                this.transitionName = 'signup-forward';
                // Collect the password immediately after the email code is verified.
                this.step = 'password';
            } catch (error) {
                const code = error?.response?.data?.code;
                if (
                    code !== BFF_AUTH_ERROR_CODES.expiredSession &&
                    (code === BFF_AUTH_ERROR_CODES.invalidOtp || [400, 401, 422].includes(error?.response?.status))
                ) {
                    (this.$refs.verifyEmail as any)?.markInvalid();
                } else this.showError(error);
            } finally {
                this.loading = false;
            }
        },
        async loadConfig() {
            // The existing public settings request already travels through the BFF's /api proxy.
            const { data } = (await this.$store.dispatch('getSignupSettings')) as { data: PublicSignUpConfig };
            const stages = [...(data.signUpStages || [])]
                .sort((a, b) => (a.Order || 0) - (b.Order || 0))
                .map((stage) => ({
                    ...stage,
                    Fields: (stage.Fields || []).map((field) => ({
                        ...field,
                        Key: field.Key || field.ID,
                        Type: field.Type === undefined ? 0 : field.Type,
                    })),
                }));
            const keys = new Set(['name', 'first_name', 'last_name']);
            const stageIds = new Set<string>();
            stages.forEach((stage) => {
                if (!stage.ID || stageIds.has(stage.ID)) throw new Error('Invalid signup configuration');
                stageIds.add(stage.ID);
                (stage.Fields || []).forEach((field) => {
                    // The gateway omits Type 0 (text) and falls back from Key to ID.
                    if (!field.Key || keys.has(field.Key) || field.Type < 0 || field.Type > 6) {
                        throw new Error('Invalid signup configuration');
                    }
                    keys.add(field.Key);
                    // Vue 2 needs $set for keys introduced after the component was created.
                    let initialValue: string | boolean | string[] = field.DefaultValue || '';
                    if (field.Type === 5) initialValue = field.DefaultValues || [];
                    if (field.Type === 3) initialValue = field.DefaultValue === 'true';
                    this.$set(this.fields, field.Key, initialValue);
                });
            });
            this.stages = stages;
        },
        setField(key: string, value: any) {
            this.$set(this.fields, key, value);
        },
        next() {
            if (this.loading) return;
            this.transitionName = 'signup-forward';
            if (this.step === 'name' && !this.nameValid) {
                (this.$refs.nameInput as any)?.inputValidation(this.fields.name);
                return;
            }
            if (this.step === 'password' && !this.validatePassword(this.password)) {
                (this.$refs.passwordInput as any)?.inputValidation(this.password);
                return;
            }
            if (this.step === 'stage' && !this.stageValid) {
                return;
            }
            this.error = '';
            if (this.step === 'password') this.step = 'name';
            else if (this.step === 'name') {
                // The name screen submits directly when the space has no configured stages.
                if (this.stages.length) this.step = 'stage';
                else this.submit();
            } else if (this.step === 'stage') {
                if (this.stageIndex < this.stages.length - 1) this.stageIndex += 1;
                else this.submit();
                this.focusHeading();
            }
        },
        back() {
            this.transitionName = 'signup-back';
            if (this.step === 'otp') this.step = 'email';
            else if (this.step === 'password') this.step = 'otp';
            else if (this.step === 'name') this.step = 'password';
            else if (this.step === 'stage') {
                if (this.stageIndex) this.stageIndex -= 1;
                else this.step = 'name';
            } else if (this.step === 'review') this.step = this.stages.length ? 'stage' : 'name';
            this.focusHeading();
        },
        async submit() {
            this.loading = true;
            this.error = '';
            try {
                const fields: Record<string, string> = {};
                Object.entries(this.fields).forEach(([key, value]) => {
                    // ponytail: comma separated multiselect values match existing profiles; add an escaped format if options may contain commas.
                    fields[key] = Array.isArray(value) ? value.join(',') : String(value);
                });
                const [firstName, ...lastName] = this.fields.name.trim().split(/\s+/u);
                fields.first_name = firstName;
                fields.last_name = lastName.join(' ');

                // Record the versions shown in the email step's legal agreement notice.
                const agreements: { version: number; type: number }[] = [];
                const { privacyPolicy, termsAndConditions } = this.$store.state;
                if (privacyPolicy?.CreatedAt) {
                    agreements.push({ version: privacyPolicy.CreatedAt, type: 0 });
                } else {
                    agreements.push({ version: 0, type: 0 });
                }
                if (termsAndConditions?.CreatedAt) {
                    agreements.push({ version: termsAndConditions.CreatedAt, type: 1 });
                } else {
                    agreements.push({ version: 0, type: 1 });
                }

                const { data } = await axios.post(
                    bff('signup/complete'),
                    {
                        email: this.email,
                        password: this.password,
                        // Forward a signup invitation only when the URL provides a single ID.
                        ...(typeof this.$route.query.invite === 'string' && this.$route.query.invite
                            ? { invite_id: this.$route.query.invite }
                            : {}),
                        fields,
                        agreements,
                    },
                    requestConfig
                );
                this.password = '';
                if (data.status === 'waiting_for_approval' || data.status === 'pending') {
                    this.step = 'waiting_for_approval';
                } else if (data.authenticated) {
                    this.$store.commit('SET_AUTH_STATE', { authenticated: true, session: data });
                    this.step = 'success';
                    // Redirect authenticated users after they have time to read the success message.
                    this.redirectTimer = setTimeout(() => this.redirectAfterSignup(), 3000);
                } else throw new Error('Unexpected signup result');
            } catch (error) {
                if (error?.response?.status === 401) {
                    this.step = 'email';
                    this.error = this.$t('messages.signupV2.restart');
                } else if (error?.response?.status === 409) this.error = this.$t('messages.signupV2.accountExists');
                else this.showError(error);
                // Surface completion failures even when the flow returns to the email step.
                EventBus.$emit('ERROR', { Message: this.error });
            } finally {
                this.loading = false;
            }
        },
        showError(error: any) {
            // Report signup failures through the same Sentry client used by login.
            Sentry.captureException(error);
            const retryAfter = Number(error?.response?.headers?.['retry-after']);
            if (error?.response?.status === 429) {
                this.error = this.$t('messages.signupV2.rateLimited', {
                    seconds: Number.isFinite(retryAfter) ? retryAfter : 0,
                });
            } else if (error?.response?.status === 401) {
                this.step = 'email';
                this.error = this.$t('messages.signupV2.restart');
            } else this.error = this.$t('messages.signupV2.error');
        },
    },
};
